import React from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Paper,
} from "@mui/material";

const PrintableReport = ({ data, filters, currentUser }) => {
  const currentDate = new Date().toLocaleDateString("ms-MY");

  // Determine title based on filters
  const getReportTitle = () => {
    let title = "LAPORAN SEMAKAN DATA PELANGGAN";
    if (currentUser?.bahagian) {
      title += ` ${currentUser.bahagian.toUpperCase()}`;
    }
    if (filters.zonharta && filters.statusakaun) {
      title += ` (${filters.zonharta.toUpperCase()}/${filters.statusakaun.toUpperCase()})`;
    } else if (filters.zonharta) {
      title += ` (${filters.zonharta.toUpperCase()})`;
    } else if (filters.statusakaun) {
      title += ` (${filters.statusakaun.toUpperCase()})`;
    } else {
      title += " (SEMUA)";
    }
    return title;
  };

  const formatCurrency = (amount) => {
    if (!amount) return "0.00";
    return parseFloat(amount).toFixed(2);
  };

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: "white",
        color: "black",
        fontFamily: "Arial, sans-serif",
        "@media print": {
          p: 2,
          boxShadow: "none",
        },
      }}
    >
      {/* Header */}
      <Box textAlign="center" mb={3}>
        <Typography variant="h6" fontWeight="bold" mb={1}>
          {getReportTitle()}
        </Typography>
        <Typography variant="body2">Tarikh: {currentDate}</Typography>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={0}>
        <Table size="small" sx={{ border: 1, borderColor: "black" }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  border: 1,
                  borderColor: "black",
                  fontWeight: "bold",
                  textAlign: "center",
                  fontSize: "0.75rem",
                  p: 0.5,
                }}
              >
                BIL
              </TableCell>
              <TableCell
                sx={{
                  border: 1,
                  borderColor: "black",
                  fontWeight: "bold",
                  textAlign: "center",
                  fontSize: "0.75rem",
                  p: 0.5,
                }}
              >
                NO. AKAUN
              </TableCell>
              <TableCell
                sx={{
                  border: 1,
                  borderColor: "black",
                  fontWeight: "bold",
                  textAlign: "center",
                  fontSize: "0.75rem",
                  p: 0.5,
                }}
              >
                NAMA
              </TableCell>
              <TableCell
                sx={{
                  border: 1,
                  borderColor: "black",
                  fontWeight: "bold",
                  textAlign: "center",
                  fontSize: "0.75rem",
                  p: 0.5,
                }}
              >
                NO. K/P
              </TableCell>
              <TableCell
                sx={{
                  border: 1,
                  borderColor: "black",
                  fontWeight: "bold",
                  textAlign: "center",
                  fontSize: "0.75rem",
                  p: 0.5,
                }}
              >
                ALAMAT
              </TableCell>
              <TableCell
                sx={{
                  border: 1,
                  borderColor: "black",
                  fontWeight: "bold",
                  textAlign: "center",
                  fontSize: "0.75rem",
                  p: 0.5,
                }}
              >
                JENIS SEWAAN
              </TableCell>
              <TableCell
                sx={{
                  border: 1,
                  borderColor: "black",
                  fontWeight: "bold",
                  textAlign: "center",
                  fontSize: "0.75rem",
                  p: 0.5,
                }}
              >
                KADAR SEWA (RM)
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data && data.length > 0 ? (
              data.map((row, index) => (
                <TableRow key={row.id || index}>
                  <TableCell
                    sx={{
                      border: 1,
                      borderColor: "black",
                      textAlign: "center",
                      fontSize: "0.7rem",
                      p: 0.3,
                    }}
                  >
                    {index + 1}
                  </TableCell>
                  <TableCell
                    sx={{
                      border: 1,
                      borderColor: "black",
                      fontSize: "0.7rem",
                      p: 0.3,
                    }}
                  >
                    {row.no_akaun || "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      border: 1,
                      borderColor: "black",
                      fontSize: "0.7rem",
                      p: 0.3,
                    }}
                  >
                    {row.customer?.nama || row.nama || "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      border: 1,
                      borderColor: "black",
                      fontSize: "0.7rem",
                      p: 0.3,
                    }}
                  >
                    {row.customer?.nokp || row.nokp || "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      border: 1,
                      borderColor: "black",
                      fontSize: "0.7rem",
                      p: 0.3,
                    }}
                  >
                    {row.harta
                      ? `${row.harta.alamat_1 || ""} ${row.harta.alamat_2 || ""} ${
                          row.harta.alamat_3 || ""
                        }`.trim()
                      : "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      border: 1,
                      borderColor: "black",
                      fontSize: "0.7rem",
                      p: 0.3,
                    }}
                  >
                    {row.harta?.kategori_kutipan || "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      border: 1,
                      borderColor: "black",
                      textAlign: "right",
                      fontSize: "0.7rem",
                      p: 0.3,
                    }}
                  >
                    {formatCurrency(row.kadar_sewa)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={7}
                  sx={{
                    border: 1,
                    borderColor: "black",
                    textAlign: "center",
                    fontSize: "0.7rem",
                    p: 1,
                  }}
                >
                  Tiada data untuk dipaparkan
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer */}
      <Box mt={2} display="flex" justifyContent="space-between" alignItems="end">
        <Typography variant="caption">Jumlah rekod: {data ? data.length : 0}</Typography>
        <Typography variant="caption">
          Dicetak pada: {new Date().toLocaleString("ms-MY")}
        </Typography>
      </Box>
    </Box>
  );
};

PrintableReport.propTypes = {
  data: PropTypes.array.isRequired,
  filters: PropTypes.object.isRequired,
  currentUser: PropTypes.object,
};

PrintableReport.defaultProps = {
  currentUser: null,
};

export default PrintableReport;
